/** Load background video only when it is needed; the responsive image remains the fallback. */
class HflVideo extends HTMLElement {
  #abort;
  #observer;
  #visible = false;
  #active = true;
  #prepared = false;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.#active = !this.hasAttribute('data-managed');
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.video.playsInline = true;
    this.video.addEventListener('playing', () => {
      if (!this.#canPlay()) return this.video.pause();
      this.video.dataset.ready = '';
      this.#prepared = false;
    }, options);
    this.video.addEventListener('error', () => { delete this.video.dataset.ready; }, options);
    this.#motion.addEventListener('change', this.#sync, options);
    navigator.connection?.addEventListener('change', this.#sync, options);
    document.addEventListener('visibilitychange', this.#sync, options);
    // Retry blocked autoplay only when this particular video is active and visible.
    document.addEventListener('pointerdown', this.#sync, { ...options, passive: true });
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting && entry.intersectionRatio > 0;
      this.#sync();
    }, { threshold: [0, 0.01] });
    this.#observer.observe(this.video);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.video?.pause();
    this.#visible = false;
  }

  get allowsAutoplay() {
    return !this.#motion.matches && !navigator.connection?.saveData;
  }

  setActive(active) {
    this.#active = active;
    this.#sync();
  }

  /** Prepares one adjacent slide, without starting playback. */
  prepare() {
    if (!this.video || !this.allowsAutoplay || document.hidden || this.video.getAttribute('src')) return;
    this.#prepared = true;
    this.#load();
  }

  cancelPreparation() {
    if (!this.#prepared || !this.video) return;
    this.#prepared = false;
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.preload = 'none';
    delete this.video.dataset.ready;
    this.video.load();
  }

  #canPlay() {
    return this.#active && this.#visible && !document.hidden && this.allowsAutoplay;
  }

  #load() {
    if (this.video.getAttribute('src')) return;
    const mobile = matchMedia('(max-width: 1023px)').matches;
    const source = (mobile && this.dataset.mobileSrc) || this.dataset.desktopSrc;
    if (!source) return;
    this.video.preload = 'auto';
    this.video.src = source;
    this.video.load();
  }

  #sync = () => {
    if (!this.video) return;
    if (this.#canPlay()) {
      this.#load();
      if (this.video.paused) this.video.play().catch(() => { /* Retain the poster. */ });
    } else {
      this.video.pause();
      if (!this.allowsAutoplay) delete this.video.dataset.ready;
      if (document.hidden || !this.allowsAutoplay) this.cancelPreparation();
    }
  };
}

if (!customElements.get('hfl-video')) customElements.define('hfl-video', HflVideo);
